// src/views/Inventory/Pembongkaran/DetailPembongkaran.js
import React, { useEffect, useState } from "react";
import {
  Card,
  Form,
  Row,
  Col,
  Select,
  Input,
  Button,
  Typography,
  Space,
  message,
  DatePicker,
  TimePicker,
  Modal,
  Spin,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  UploadOutlined,
  PlusOutlined,
  SaveOutlined,
  RollbackOutlined,
} from "@ant-design/icons";
import { useParams } from "react-router-dom";
import axiosInstance from "../../../../configs";
import Title from "antd/es/typography/Title";
import dayjs from "dayjs";
import {
  autoCorrectLotNumber,
  LOT_NUMBER_FORMAT,
} from "../../../../utils/lotNumberValidator";

const { Text } = Typography;
const { Option } = Select;

const DOC_OPTIONS = [
  { label: "BC 1.6", value: 16 },
  { label: "BC 2.7", value: 27 },
  { label: "BC 4.0", value: 40 },
];

export default function DetailPembongkaran() {
  const [form] = Form.useForm();
  const { idStripping } = useParams(); // SESUAIKAN PARAM ROUTE KAMU

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [nopenOptions, setNopenOptions] = useState([]);
  const [loadingNopen, setLoadingNopen] = useState(false);
  const [loadingDetailNopen, setLoadingDetailNopen] = useState(false);

  // simpan header incoming dari stripping.header (untuk nopen, id_incoming)
  const [incomingHeader, setIncomingHeader] = useState(null);
  const [strippingHeader, setStrippingHeader] = useState(null);
  const [originalFormValues, setOriginalFormValues] = useState(null);

  // Confirmation Modal State
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);

  // State untuk menyimpan warning Nomor Lot per blok
  // Key: "barangIndex-blockIndex", Value: warning message
  const [lotWarnings, setLotWarnings] = useState({});

  /**
   * Handler untuk auto-correct dan validasi Nomor Lot saat onBlur
   * @param {number} barangIndex - Index barang
   * @param {number} blockIndex - Index blok
   * @param {string} value - Nilai input
   */
  const handleLotNumberBlur = (barangIndex, blockIndex, value) => {
    if (!value) {
      // Clear warning jika kosong
      setLotWarnings((prev) => {
        const newWarnings = { ...prev };
        delete newWarnings[`${barangIndex}-${blockIndex}`];
        return newWarnings;
      });
      return;
    }

    const result = autoCorrectLotNumber(value);
    const warningKey = `${barangIndex}-${blockIndex}`;

    if (result.isValid) {
      // Auto-correct dan clear warning
      const barang = form.getFieldValue("barang") || [];
      if (
        barang[barangIndex] &&
        barang[barangIndex].blocks &&
        barang[barangIndex].blocks[blockIndex]
      ) {
        barang[barangIndex].blocks[blockIndex].lotNumber = result.corrected;
        form.setFieldsValue({ barang });
        message.success(`Format Nomor Lot diperbaiki: ${result.corrected}`);
      }
      setLotWarnings((prev) => {
        const newWarnings = { ...prev };
        delete newWarnings[warningKey];
        return newWarnings;
      });
    } else {
      // Set warning
      setLotWarnings((prev) => ({
        ...prev,
        [warningKey]: result.warning,
      }));
      message.warning(`Format Nomor Lot tidak valid: ${result.warning}`);
    }
  };

  // ===== GENERIC: tally handler (sama seperti form create) =====
  const handleTallyChange = (value, rowIndex) => {
    const barang = form.getFieldValue("barang") || [];
    const row = barang[rowIndex] || {};

    const jumlahKemasan = Number(row.kemasan || 0);
    const tally = Number(value || 0);

    let catatan = "";
    let revisi = "";
    if (!Number.isNaN(jumlahKemasan) && !Number.isNaN(tally)) {
      if (tally === jumlahKemasan) {
        catatan = "SESUAI";
        revisi = "N";
      } else if (tally > jumlahKemasan) {
        catatan = `LEBIH ${tally - jumlahKemasan}`;
        revisi = "Y";
      } else {
        catatan = `KURANG ${jumlahKemasan - tally}`;
        revisi = "Y";
      }
    }

    barang[rowIndex] = {
      ...row,
      tallyKemasan: value,
      catatan,
      revisi,
    };

    form.setFieldsValue({ barang });
  };

  // ===== LOAD DETAIL STRIPPING BY ID =====
  const fetchDetailStripping = async () => {
    setLoading(true);
    try {
      // ASUMSI API:
      // GET /api/stripping/:idStripping
      const res = await axiosInstance.get(
        `/stripping/${idStripping}/with-details`,
      );

      const header = res.data?.header || {};
      const details = res.data?.details || [];

      setStrippingHeader(header);

      // simpan incoming minimal untuk nopen
      setIncomingHeader({
        id_incoming: header.id_incoming,
        document_number: header.document_number,
      });

      // GROUPING details by (seri + item_code)
      const barangMap = new Map();
      details.forEach((d) => {
        const key = `${d.seri}||${d.item_code}`;
        if (!barangMap.has(key)) {
          barangMap.set(key, {
            seri: d.seri,
            itemCode: d.item_code,
            itemName: d.item_name
              ? `${d.item_name} (${d.item_code})`
              : d.item_code,
            kemasan: d.qty_kemasan,
            tallyKemasan: 0,
            catatan: d.catatan || "",
            revisi: d.revisi || "N",
            blocks: [],
          });
        }
        const row = barangMap.get(key);
        row.blocks.push({
          id_stripping_detail: d.id_stripping_detail,
          gc: d.id_warehouse || null,
          lotNumber: d.gc || null,
          qty: d.qty_stripping,
          notes: d.catatan || "",
        });
      });

      const barang = Array.from(barangMap.values()).map((row) => {
        const totalTally = (row.blocks || []).reduce(
          (sum, b) => sum + Number(b.qty || 0),
          0,
        );
        return {
          ...row,
          tallyKemasan: totalTally,
        };
      });

      const platNumberArr = header.no_polisi
        ? String(header.no_polisi)
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [];

      const formVals = {
        strippingNumber: header.stripping_number,
        strippingDate: header.stripping_date
          ? dayjs(header.stripping_date)
          : null,
        jamMulai: header.jam_mulai ? dayjs(header.jam_mulai, "HH:mm") : null,
        jamSelesai: header.jam_selesai
          ? dayjs(header.jam_selesai, "HH:mm")
          : null,
        id_document: header.id_document || undefined,
        document_number: header.document_number || undefined,
        platNumber: platNumberArr,
        sealNumber: header.no_container || "",
        barang,
      };

      form.setFieldsValue(formVals);
      setOriginalFormValues(formVals);
    } catch (err) {
      console.error(err);
      message.error("Gagal memuat detail pembongkaran");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (idStripping) {
      fetchDetailStripping();
    }
  }, [idStripping]);

  // ===== (OPSIONAL) LOAD NOPEN IF KAMU MASIH MAU EDIT DOC PABEAN =====
  const handleJenisDokumenChange = async (idDocument) => {
    form.setFieldsValue({ document_number: undefined });
    if (!idDocument) {
      setNopenOptions([]);
      return;
    }
    setLoadingNopen(true);
    try {
      const res = await axiosInstance.get("/incoming/getListNopen", {
        params: { id_document: idDocument },
      });

      const list = res.data?.data || [];
      setNopenOptions(
        list.map((item) => ({
          label: `${item.document_number} - ${item.importir_name} (${dayjs(item.document_date).format("DD MMM YYYY")})`,
          // Use a unique value for the Select to distinguish between collisions
          value: `${item.document_number}|${dayjs(item.document_date).format("YYYY-MM-DD")}|${item.id_document}`,
        })),
      );
    } catch (err) {
      console.error(err);
      message.error("Gagal mengambil daftar No Dokumen Pabean");
      setNopenOptions([]);
    } finally {
      setLoadingNopen(false);
    }
  };

  const handleDocumentNumberChange = async (compositeValue) => {
    // Kalau mau re-load dari incoming lagi saat ubah dokumen di mode edit.
    if (!compositeValue) return;

    const [noDocument, docDate, idDoc] = compositeValue.split("|");
    form.setFieldsValue({ document_number: compositeValue });

    setLoadingDetailNopen(true);
    try {
      const res = await axiosInstance.get("/incoming/getDetilNopen", {
        params: {
          no_document: noDocument,
          id_document: idDoc,
          document_date: docDate,
        },
      });

      const incoming = res.data?.header || null;
      const details = res.data?.details || [];
      setIncomingHeader(incoming);

      if (!details.length) {
        message.warning("Tidak ada detail barang untuk dokumen ini");
        form.setFieldsValue({ barang: [] });
        return;
      }

      const barangFromDetails = details.map((d) => ({
        seri: d.seri,
        itemCode: d.item_code,
        itemName: `${d.item_name || d.item_code} (${d.item_code})`,
        kemasan: d.total_kemasan,
        tallyKemasan: undefined,
        catatan: "",
        revisi: "N",
        blocks: [
          {
            gc: undefined,
            lotNumber: undefined,
            qty: undefined,
            notes: undefined,
          },
        ],
      }));

      form.setFieldsValue({ barang: barangFromDetails });
    } catch (err) {
      console.error(err);
      message.error("Gagal mengambil detail Nopen");
    } finally {
      setLoadingDetailNopen(false);
    }
  };

  // ===== EDIT TOGGLE =====
  const handleStartEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    if (originalFormValues) {
      form.setFieldsValue(originalFormValues);
    }
    setIsEditing(false);
  };

  const handleBack = () => {
    window.history.back();
  };

  // ===== SUBMIT UPDATE =====
  const onFinish = async (values) => {
    if (!strippingHeader || !strippingHeader.id_stripping) {
      message.error("Data stripping tidak valid");
      return;
    }
    setPendingValues(values);
    setConfirmVisible(true);
  };

  const handleConfirmSubmit = async () => {
    const values = pendingValues;

    try {
      setSaving(true);
      const barang = values.barang || [];

      const totalKemasanHeader = barang.reduce((sum, row) => {
        const v = Number(row.kemasan || 0);
        return sum + (Number.isNaN(v) ? 0 : v);
      }, 0);

      const detailsPayload = [];
      barang.forEach((row) => {
        const blocks =
          row.blocks && row.blocks.length
            ? row.blocks
            : [{ gc: null, qty: null, notes: null }];

        blocks.forEach((block) => {
          detailsPayload.push({
            id_stripping_detail: block.id_stripping_detail || 0, // dari GET
            id_stripping: strippingHeader.id_stripping,
            seri: row.seri,
            nopen: incomingHeader ? incomingHeader.document_number : null,
            tanggal_nopen: incomingHeader
              ? dayjs(incomingHeader.document_date).format("YYYY-MM-DD")
              : null,
            item_code: row.itemCode || null,
            id_warehouse: block.gc || null,
            gc: block.lotNumber || null,
            qty_kemasan: Number(row.kemasan || 0),
            qty_stripping: Number(block.qty || 0),
            catatan: block.notes || row.catatan || null,
            revisi: row.revisi || "N",
          });
        });
      });

      const headerPayload = {
        id_stripping: strippingHeader.id_stripping,
        id_incoming: incomingHeader ? incomingHeader.id_incoming : null,
        stripping_number:
          values.strippingNumber || strippingHeader.stripping_number,
        stripping_date: values.strippingDate
          ? dayjs(values.strippingDate).format("YYYY-MM-DD")
          : null,
        jam_mulai: values.jamMulai
          ? dayjs(values.jamMulai).format("HH:mm")
          : null,
        jam_selesai: values.jamSelesai
          ? dayjs(values.jamSelesai).format("HH:mm")
          : null,
        no_container: values.sealNumber || strippingHeader.no_container || null,
        vendor_officer: strippingHeader.vendor_officer || "BUDIMAN",
        total_kemasan: totalKemasanHeader,
        notes: strippingHeader.notes || null,
        document_number: values.document_number
          ? values.document_number.split("|")[0]
          : strippingHeader.document_number || null,
        lampiran_1: strippingHeader.lampiran_1 || null,
        lampiran_2: strippingHeader.lampiran_2 || null,
        no_polisi: Array.isArray(values.platNumber)
          ? values.platNumber.join(",")
          : values.platNumber || strippingHeader.no_polisi || null,
        created_by: strippingHeader.created_by || 0,
        updated_by: 0,
      };

      const payload = {
        header: headerPayload,
        details: detailsPayload,
      };

      // ASUMSI UPDATE: PUT /api/stripping/:id
      await axiosInstance.put(
        `/stripping/${strippingHeader.id_stripping}`,
        payload,
      );

      message.success("Perubahan data stripping berhasil disimpan");
      setIsEditing(false);
      setIsEditing(false);
      // refresh original values
      setOriginalFormValues(values);
      setConfirmVisible(false);
    } catch (err) {
      console.error(err);
      message.error("Gagal menyimpan perubahan stripping");
    } finally {
      setSaving(false);
    }
  };

  const readOnly = !isEditing;

  return (
    <Card className="kt-portlet" bodyStyle={{ padding: 24 }}>
      {/* HEADER TITLE + ACTIONS */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={3} style={{ marginBottom: 4 }}>
            Detail Pembongkaran Barang
          </Title>
          <Text type="secondary">
            Lihat dan ubah data Pembongkaran / Stripping barang.
          </Text>
        </Col>
        <Col>
          <Space>
            <Button onClick={handleBack}>Kembali</Button>
            {!isEditing && (
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={handleStartEdit}
                disabled={loading}
              >
                Edit
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        disabled={loading} // disable total kalau lagi loading
      >
        {/* ---------- BARIS ATAS: DOKUMEN PABEAN ---------- */}
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>Nomor Tallysheet</Text>}
              name="strippingNumber"
            >
              <Input placeholder="No Tallysheet" disabled />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>Tanggal Tallysheet</Text>}
              name="strippingDate"
              rules={[
                { required: true, message: "Tanggal Tallysheet wajib diisi" },
              ]}
            >
              <DatePicker
                style={{ width: "100%" }}
                format="DD MMMM YYYY"
                disabled={readOnly}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>Jenis Dokumen Pabean</Text>}
              name="id_document"
            >
              <Select
                placeholder="Jenis Dokumen Pabean"
                options={DOC_OPTIONS}
                onChange={handleJenisDokumenChange}
                disabled={readOnly}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>No Dokumen Pabean</Text>}
              name="document_number"
            >
              <Select
                placeholder="Pilih Nomor Dokumen Pabean"
                loading={loadingNopen || loadingDetailNopen}
                showSearch
                optionFilterProp="label"
                onChange={handleDocumentNumberChange}
                disabled={readOnly}
              >
                {nopenOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* ---------- BARIS KEDUA: PLAT & SEGEL ---------- */}
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>No Plat Kendaraan</Text>}
              name="platNumber"
              extra="Pisahkan dengan Enter"
            >
              <Select
                mode="tags"
                placeholder="No Plat Kendaraan"
                open={false}
                disabled={readOnly}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>Nomor Segel</Text>}
              name="sealNumber"
            >
              <Input placeholder="No Segel Kendaraan" disabled={readOnly} />
            </Form.Item>
          </Col>
        </Row>

        {/* ---------- BARIS KETIGA: JAM MULAI & SELESAI ---------- */}
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>Jam Mulai Pembongkaran</Text>}
              name="jamMulai"
            >
              <TimePicker
                style={{ width: "100%" }}
                format="HH:mm"
                placeholder="Pilih Jam Mulai"
                disabled={readOnly}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>Jam Selesai Pembongkaran</Text>}
              name="jamSelesai"
            >
              <TimePicker
                style={{ width: "100%" }}
                format="HH:mm"
                placeholder="Pilih Jam Selesai"
                disabled={readOnly}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* ---------- LAMPIRAN / UPLOAD (MASIH DUMMY) ---------- */}
        <Row style={{ marginTop: 8, marginBottom: 8 }}>
          <Col span={24}>
            <Text strong>Lampiran</Text>
          </Col>
          <Col span={24} style={{ marginTop: 8 }}>
            <Space direction="vertical" size={4}>
              <Button
                type="primary"
                icon={<UploadOutlined />}
                style={{
                  background: "#FFA800",
                  borderColor: "#FFA800",
                  fontWeight: 600,
                }}
                disabled={readOnly}
                onClick={() => {
                  if (readOnly) return;
                  document.getElementById("upload-lampiran-stripping")?.click();
                }}
              >
                Upload File
              </Button>
              <input
                id="upload-lampiran-stripping"
                type="file"
                style={{ display: "none" }}
                accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    message.success(
                      `File "${file.name}" siap diupload (handle upload terpisah).`,
                    );
                  }
                }}
              />
              <Text type="secondary">
                Jenis File: JPG | JPEG | PNG | GIF | PDF | DOC | DOCX | XLS |
                XLSX
                <br />
                Ukuran Maksimal File: 5 MB.
              </Text>
            </Space>
          </Col>
        </Row>

        {/* ---------- TABEL BARANG + BLOK / LOT ---------- */}
        <Spin spinning={loading} tip="Memuat data pembongkaran..." size="large">
          <Card
            bodyStyle={{ padding: 0 }}
            style={{
              marginTop: 36,
              borderRadius: 2,
              border: "1px solid #E4E6EF",
              overflow: "hidden",
            }}
          >
            {/* Header tabel */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "0.8fr 3fr 1.2fr 1.2fr 1.8fr 80px",
                background: "#F3F6F9",
                padding: "8px 16px",
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              <div>SERI</div>
              <div>NAMA BARANG *</div>
              <div>JUMLAH KEMASAN</div>
              <div>TALLYING KEMASAN</div>
              <div>CATATAN</div>
              <div style={{ textAlign: "center" }}>AKSI</div>
            </div>

            <Form.List name="barang">
              {(fields, { remove }) => (
                <>
                  {fields.map((field) => (
                    <div key={field.key}>
                      {/* Row utama barang */}
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "0.8fr 3fr 1.2fr 1.2fr 1.8fr 80px",
                          padding: "8px 16px",
                          borderTop: "1px solid #E4E6EF",
                          fontSize: 13,
                          alignItems: "center",
                          background: "#fff",
                        }}
                      >
                        {/* Seri */}
                        <Form.Item
                          {...field}
                          name={[field.name, "seri"]}
                          fieldKey={[field.fieldKey, "seri"]}
                          style={{
                            marginBottom: 0,
                            paddingRight: 8,
                          }}
                        >
                          <Input disabled placeholder="Seri" />
                        </Form.Item>

                        {/* Nama Barang */}
                        <Form.Item
                          {...field}
                          name={[field.name, "itemName"]}
                          fieldKey={[field.fieldKey, "itemName"]}
                          style={{ marginBottom: 0, paddingRight: 8 }}
                        >
                          <Input disabled placeholder="Nama Barang" />
                        </Form.Item>

                        {/* Jumlah Kemasan */}
                        <Form.Item
                          {...field}
                          name={[field.name, "kemasan"]}
                          fieldKey={[field.fieldKey, "kemasan"]}
                          style={{ marginBottom: 0, paddingRight: 8 }}
                        >
                          <Input disabled placeholder="Jumlah Kemasan" />
                        </Form.Item>

                        {/* Tallying Kemasan */}
                        <Form.Item
                          {...field}
                          name={[field.name, "tallyKemasan"]}
                          fieldKey={[field.fieldKey, "tallyKemasan"]}
                          style={{ marginBottom: 0, paddingRight: 8 }}
                        >
                          <Input
                            placeholder="Tally Sheet"
                            disabled={readOnly}
                            onChange={(e) =>
                              handleTallyChange(e.target.value, field.name)
                            }
                          />
                        </Form.Item>

                        {/* Catatan */}
                        <Form.Item
                          {...field}
                          name={[field.name, "catatan"]}
                          fieldKey={[field.fieldKey, "catatan"]}
                          style={{ marginBottom: 0, paddingRight: 8 }}
                        >
                          <Input placeholder="Catatan" disabled />
                        </Form.Item>

                        <div style={{ textAlign: "center" }}>
                          <Button
                            type="text"
                            icon={
                              <DeleteOutlined style={{ color: "#F64E60" }} />
                            }
                            onClick={() => remove(field.name)}
                            disabled={readOnly}
                          />
                        </div>
                      </div>

                      {/* Detail Blok / Lot */}
                      <div
                        style={{
                          borderTop: "1px solid #F3F6F9",
                          padding: "8px 16px 16px",
                        }}
                      >
                        <Text strong>
                          <i>Detail Blok / Lot</i>
                        </Text>

                        <Form.List name={[field.name, "blocks"]}>
                          {(
                            blockFields,
                            { add: addBlock, remove: removeBlock },
                          ) => (
                            <>
                              {blockFields.map((blockField) => (
                                <Row
                                  key={blockField.key}
                                  gutter={8}
                                  style={{ marginTop: 8, alignItems: "center" }}
                                >
                                  {/* hidden id_stripping_detail */}
                                  <Form.Item
                                    {...blockField}
                                    name={[
                                      blockField.name,
                                      "id_stripping_detail",
                                    ]}
                                    fieldKey={[
                                      blockField.fieldKey,
                                      "id_stripping_detail",
                                    ]}
                                    hidden
                                  />

                                  <Col xs={24} md={5}>
                                    <Form.Item
                                      {...blockField}
                                      name={[blockField.name, "gc"]}
                                      fieldKey={[blockField.fieldKey, "gc"]}
                                      style={{ marginBottom: 0 }}
                                    >
                                      <Select
                                        placeholder="Rak Tujuan"
                                        disabled={readOnly}
                                      >
                                        <Option value="A">A</Option>
                                        <Option value="B">B</Option>
                                        <Option value="C">C</Option>
                                        <Option value="D">D</Option>
                                        <Option value="E">E</Option>
                                        <Option value="F">F</Option>
                                        <Option value="G">G</Option>
                                        <Option value="H">H</Option>
                                        <Option value="I">I</Option>
                                        <Option value="J">J</Option>
                                        <Option value="K">K</Option>
                                        <Option value="L">L</Option>
                                        <Option value="M">M</Option>
                                        <Option value="N">N</Option>
                                        <Option value="O">O</Option>
                                        <Option value="CL">CL (Chiller)</Option>
                                      </Select>
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24} md={5}>
                                    <Form.Item
                                      {...blockField}
                                      name={[blockField.name, "lotNumber"]}
                                      fieldKey={[
                                        blockField.fieldKey,
                                        "lotNumber",
                                      ]}
                                      style={{ marginBottom: 0 }}
                                      help={
                                        lotWarnings[
                                          `${field.name}-${blockField.name}`
                                        ] ? (
                                          <span
                                            style={{
                                              color: "#faad14",
                                              fontSize: 11,
                                            }}
                                          >
                                            ⚠️{" "}
                                            {
                                              lotWarnings[
                                                `${field.name}-${blockField.name}`
                                              ]
                                            }
                                          </span>
                                        ) : null
                                      }
                                      validateStatus={
                                        lotWarnings[
                                          `${field.name}-${blockField.name}`
                                        ]
                                          ? "warning"
                                          : undefined
                                      }
                                      tooltip="Format: {Blok}{Posisi}.{Nomor} - Contoh: AG.1, BX.20"
                                    >
                                      <Input
                                        placeholder="Nomor Lot (cth: AG.1)"
                                        disabled={readOnly}
                                        onBlur={(e) =>
                                          handleLotNumberBlur(
                                            field.name,
                                            blockField.name,
                                            e.target.value,
                                          )
                                        }
                                        style={
                                          lotWarnings[
                                            `${field.name}-${blockField.name}`
                                          ]
                                            ? {
                                                borderColor: "#faad14",
                                                backgroundColor: "#fffbe6",
                                              }
                                            : undefined
                                        }
                                      />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24} md={5}>
                                    <Form.Item
                                      {...blockField}
                                      name={[blockField.name, "qty"]}
                                      fieldKey={[blockField.fieldKey, "qty"]}
                                      style={{ marginBottom: 0 }}
                                    >
                                      <Input
                                        placeholder="Jumlah Barang"
                                        disabled={readOnly}
                                      />
                                    </Form.Item>
                                  </Col>
                                  <Col xs={24} md={7}>
                                    <Form.Item
                                      {...blockField}
                                      name={[blockField.name, "notes"]}
                                      fieldKey={[blockField.fieldKey, "notes"]}
                                      style={{ marginBottom: 0 }}
                                    >
                                      <Input
                                        placeholder="Catatan"
                                        disabled={readOnly}
                                      />
                                    </Form.Item>
                                  </Col>
                                  <Col
                                    xs={24}
                                    md={2}
                                    style={{ textAlign: "center" }}
                                  >
                                    <Button
                                      type="text"
                                      icon={
                                        <DeleteOutlined
                                          style={{ color: "#F64E60" }}
                                        />
                                      }
                                      onClick={() =>
                                        removeBlock(blockField.name)
                                      }
                                      disabled={readOnly}
                                    />
                                  </Col>
                                </Row>
                              ))}

                              {isEditing && (
                                <Button
                                  type="dashed"
                                  size="small"
                                  onClick={() =>
                                    addBlock({
                                      gc: undefined,
                                      lotNumber: undefined,
                                      qty: undefined,
                                      notes: undefined,
                                    })
                                  }
                                  style={{ marginTop: 8 }}
                                  icon={<PlusOutlined />}
                                >
                                  Tambah Blok
                                </Button>
                              )}
                            </>
                          )}
                        </Form.List>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </Form.List>
          </Card>
        </Spin>

        {/* tombol simpan / batal */}
        <Row justify="end" style={{ marginTop: 16 }}>
          <Space>
            <Button onClick={handleBack}>Kembali</Button>

            {isEditing && (
              <>
                <Button
                  icon={<RollbackOutlined />}
                  onClick={handleCancelEdit}
                  disabled={saving}
                >
                  Batal Edit
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={saving}
                  icon={<SaveOutlined />}
                >
                  Simpan Perubahan
                </Button>
              </>
            )}
          </Space>
        </Row>
      </Form>

      {/* Confirmation Modal */}
      <Modal
        title="Konfirmasi Perubahan Data"
        open={confirmVisible}
        onOk={handleConfirmSubmit}
        onCancel={() => setConfirmVisible(false)}
        okText="Ya, Simpan Perubahan"
        cancelText="Batal"
        confirmLoading={saving}
        width={600}
      >
        {pendingValues && (
          <div>
            <p>
              Apakah Anda yakin ingin menyimpan perubahan data pembongkaran ini?
            </p>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginBottom: 16,
              }}
            >
              <tbody>
                <tr>
                  <td style={{ padding: 4, fontWeight: "bold" }}>
                    No Tallysheet
                  </td>
                  <td style={{ padding: 4 }}>
                    : {pendingValues.strippingNumber}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: 4, fontWeight: "bold" }}>Tanggal</td>
                  <td style={{ padding: 4 }}>
                    :{" "}
                    {pendingValues.strippingDate
                      ? dayjs(pendingValues.strippingDate).format(
                          "DD MMMM YYYY",
                        )
                      : "-"}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: 4, fontWeight: "bold" }}>No Dokumen</td>
                  <td style={{ padding: 4 }}>
                    : {pendingValues.document_number}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: 4, fontWeight: "bold" }}>Jam Mulai</td>
                  <td style={{ padding: 4 }}>
                    :{" "}
                    {pendingValues.jamMulai
                      ? dayjs(pendingValues.jamMulai).format("HH:mm")
                      : "-"}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: 4, fontWeight: "bold" }}>
                    Jam Selesai
                  </td>
                  <td style={{ padding: 4 }}>
                    :{" "}
                    {pendingValues.jamSelesai
                      ? dayjs(pendingValues.jamSelesai).format("HH:mm")
                      : "-"}
                  </td>
                </tr>
              </tbody>
            </table>

            <Text strong>Ringkasan Barang:</Text>
            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
              {(pendingValues.barang || []).map((item, idx) => (
                <li key={idx} style={{ marginBottom: 8 }}>
                  <div>
                    {item.itemName} - <b>{item.tallyKemasan}</b> /{" "}
                    {item.kemasan} Kemasan
                    {item.catatan && item.catatan !== "SESUAI" && (
                      <span style={{ color: "red", marginLeft: 8 }}>
                        ({item.catatan})
                      </span>
                    )}
                  </div>
                  {item.blocks && item.blocks.length > 0 && (
                    <div style={{ fontSize: 12, color: "#888", marginLeft: 0 }}>
                      Lokasi:{" "}
                      {item.blocks
                        .map(
                          (b) =>
                            `${b.gc || "?"} (Lot: ${b.lotNumber || "-"}, Qty: ${
                              b.qty || 0
                            })`,
                        )
                        .join(", ")}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Modal>
    </Card>
  );
}
